import cloneDeep from 'lodash/cloneDeep';
import omit from 'lodash/omit';

export enum ActionType {
  Undo,
  Redo,
  UpdateRemovedWidgets,
}

interface StateItem {
  [key: string]: string;
  history: string;
}
export interface State {
  past: StateItem[];
  present: StateItem;
  future: StateItem[];
  type?: string;
}

export default function undoable(
  reducer,
  skip: (compare: { type: string; prevType: string }) => boolean = () => false,
  ignoreProps = []
) {
  // Call the reducer with empty action to populate the initial state
  const initialState: State = {
    past: [],
    present: reducer(undefined, {}),
    future: [],
    type: '',
  };

  // Return a reducer that handles undo and redo
  return (state = initialState, action = undefined) => {
    const { past, present, future, type: prevType } = state;
    const { type, payload } = action;

    switch (type) {
      case ActionType.Undo: {
        const previous = past[past.length - 1];
        previous.history = present.history;
        const newPast = past.slice(0, past.length - 1);
        return {
          past: newPast,
          present: previous,
          future: [present, ...future],
        };
      }
      case ActionType.Redo: {
        const next = future[0];
        next.history = present.history;
        const newFuture = future.slice(1);
        return {
          past: [...past, present],
          present: next,
          future: newFuture,
        };
      }
      case ActionType.UpdateRemovedWidgets: {
        const update = items =>
          items.map(item => {
            const widgets = item.widgets.map(widget => {
              const exist = payload.widgets.find(({ id }) => id === widget.id);
              if (!exist) {
                return { ...widget, deleted: true };
              }
              return widget;
            });
            return { ...item, widgets };
          });
        return { ...state, past: [...update(state.past)], future: [...update(state.future)] };
      }
      default: {
        const oldPresent = cloneDeep(omit(present, ignoreProps));
        const newPresent = reducer(present ?? state, action);
        if (present === newPresent) {
          return state;
        }

        if (skip({ type, prevType })) {
          return {
            type,
            past,
            present: newPresent,
            future,
          };
        }

        return {
          type,
          past: [...(past ?? []), oldPresent],
          present: newPresent,
          future: [],
        };
      }
    }
  };
}
